import type { CharacterConfig, MissionPhase } from '../types/content';
import type { ScoreBreakdown } from './systems/ScoreSystem';

export interface HudChaseState {
  active: boolean;
  released: boolean;
  distancePx: number;
  graceMs: number;
}

export interface HudBossState {
  active: boolean;
  name: string;
  hp: number;
  maxHp: number;
  phase: 'telegraph' | 'charge' | 'vulnerable' | 'patrol' | 'down';
  hint: string;
}

export interface HudState {
  health: number;
  wanted: number;
  objective: string;
  missionPhase: MissionPhase;
  tokens: number;
  abilityReady: boolean;
  abilityLabel: string;
  abilityHint: string;
  abilityStatus: string;
  canCrouch: boolean;
  elapsedMs: number;
  checkpointLabel?: string;
  message?: string;
  chase?: HudChaseState;
  boss?: HudBossState;
}

export interface CompletionState {
  levelId: string;
  levelName: string;
  characterId: CharacterConfig['id'];
  characterName: string;
  elapsedMs: number;
  tokens: number;
  respawns: number;
  score: ScoreBreakdown;
  personalBest: number;
  isNewBest: boolean;
  missionScore: number;
  stagesCleared: number;
  verificationStatus: 'local' | 'submitting' | 'submitted' | 'review' | 'failed';
}

interface GameEventMap {
  'game:ready': undefined;
  'game:hud': HudState;
  'game:started': { characterId: CharacterConfig['id']; levelId: string };
  'game:paused': { paused: boolean };
  'game:complete': CompletionState;
  'game:verification': { status: CompletionState['verificationStatus']; score?: number };
  'game:toast': { message: string };
  'game:audio': { kind: 'token' | 'damage' | 'ability' | 'checkpoint' | 'chase' | 'complete' };
  'ui:start-game': { characterId: CharacterConfig['id']; levelId: string; resume: boolean };
  'ui:pause': undefined;
  'ui:resume': undefined;
  'ui:restart': undefined;
  'ui:menu': undefined;
  'input:reset': undefined;
  'input:left': { active: boolean };
  'input:right': { active: boolean };
  'input:jump': { active: boolean };
  'input:crouch': { active: boolean };
  'input:ability': { active: boolean };
  'input:roll': { active: boolean };
}

export type GameEventName = keyof GameEventMap;

class TypedEventBus {
  private readonly target = new EventTarget();

  emit<K extends GameEventName>(name: K, detail: GameEventMap[K]): void {
    this.target.dispatchEvent(new CustomEvent(name, { detail }));
  }

  on<K extends GameEventName>(name: K, listener: (detail: GameEventMap[K]) => void): () => void {
    const wrapped = (event: Event) => listener((event as CustomEvent<GameEventMap[K]>).detail);
    this.target.addEventListener(name, wrapped);
    return () => this.target.removeEventListener(name, wrapped);
  }
}

export const gameEvents = new TypedEventBus();
